import fs from 'node:fs';
import path from 'node:path';

/**
 * Format a value for an env file. Single-line values are written bare; values
 * with newlines (e.g. a PEM CA cert), quotes, or carriage returns are
 * double-quoted with `\n` escapes so the file stays one-key-per-line and
 * dotenv reads them back as the original multi-line string.
 */
function formatEnvValue(value: string): string {
  if (!/[\n\r"]/.test(value)) return value;
  const escaped = value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\r/g, '')
    .replace(/\n/g, '\\n');
  return `"${escaped}"`;
}

/**
 * Merge KEY=value updates into an env file non-destructively: lines the
 * user added (comments, other keys) are preserved; managed keys are
 * updated in place or appended.
 */
export function mergeEnvFile(filePath: string, updates: Record<string, string>): void {
  let existing = '';
  if (fs.existsSync(filePath)) {
    existing = fs.readFileSync(filePath, 'utf8');
  }
  const lines = existing.length > 0 ? existing.split(/\r?\n/) : [];
  const seen = new Set<string>();
  const out: string[] = [];

  for (const line of lines) {
    const m = line.match(/^([A-Za-z_][A-Za-z0-9_]*)=/);
    if (m && updates[m[1]] !== undefined) {
      out.push(`${m[1]}=${formatEnvValue(updates[m[1]])}`);
      seen.add(m[1]);
    } else {
      out.push(line);
    }
  }

  for (const [key, value] of Object.entries(updates)) {
    if (!seen.has(key)) {
      if (out.length > 0 && out[out.length - 1] !== '') out.push('');
      out.push(`${key}=${formatEnvValue(value)}`);
    }
  }

  while (out.length > 0 && out[out.length - 1] === '') out.pop();
  fs.mkdirSync(path.dirname(filePath), { recursive: true });
  fs.writeFileSync(filePath, `${out.join('\n')}\n`);
  try {
    fs.chmodSync(filePath, 0o600);
  } catch {
    /* Windows: skip silently */
  }
}
